import React, { useState, useEffect } from 'react';

const hexToRgb = (hex) => hex.replace('#', '').match(/.{2}/g).map(h => parseInt(h, 16));

const darken = (hex, amt) => {
    const [r, g, b] = hexToRgb(hex);
    return '#' + [r, g, b].map(c => Math.max(0, Math.round(c * (1 - amt))).toString(16).padStart(2, '0')).join('');
};

const lighten = (hex, amt) => {
    const [r, g, b] = hexToRgb(hex);
    return '#' + [r, g, b].map(c => Math.min(255, Math.round(c + (255 - c) * amt)).toString(16).padStart(2, '0')).join('');
};

const WIDGET_CSS = `
  .thermo-widget-container { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px; line-height: 1.4; box-sizing: border-box; }
  .thermo-widget-container * { box-sizing: border-box; }

  /* -- Base widget -- */
  .thermo-widget { display: flex; flex-direction: column; align-items: center; }
  .thermo-body { display: flex; flex-direction: column; align-items: center; position: relative; }
  .thermo-tube { position: relative; z-index: 1; overflow: hidden; background: #eee; border-radius: 99px 99px 0 0; }
  .thermo-widget.dark .thermo-tube { background: #333; }
  .thermo-fill { position: absolute; bottom: 0; left: 0; right: 0; background: var(--w-accent); border-radius: 99px 99px 0 0; height: 0%; transition: height 1.4s cubic-bezier(0.22, 1, 0.36, 1); }

  /* -- Bulb -- */
  .thermo-bulb { border-radius: 50%; background: var(--w-accent); display: flex; align-items: center; justify-content: center; position: relative; z-index: 3; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
  .thermo-bulb-ring { border-radius: 50%; background: rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; }
  .thermo-bulb-inner { border-radius: 50%; background: rgba(255,255,255,0.15); }

  /* -- Pct label on fill -- */
  .thermo-pct { position: absolute; top: -2px; left: 50%; transform: translate(-50%, -100%); font-size: 10px; font-weight: 700; color: var(--w-accent); background: none; padding: 2px 8px; border-radius: 99px; white-space: nowrap; z-index: 3; opacity: 0; transition: opacity 0.4s ease; }
  .thermo-pct.visible { opacity: 1; }

  /* -- Stats -- */
  .thermo-stats { text-align: center; }
  .thermo-amount { font-weight: 700; color: #1a1a2e; letter-spacing: -0.02em; }
  .thermo-raised-label { font-weight: 500; color: #888; }
  .thermo-goal { font-size: 12px; font-weight: 500; color: #888; }
  .thermo-goal strong { color: #555; font-weight: 600; }
  .thermo-donors { font-size: 12px; font-weight: 500; color: #888; margin-top: 2px; }
  .thermo-donors strong { color: #555; font-weight: 700; }
  .thermo-campaign { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--w-accent); margin-bottom: 4px; }

  .thermo-widget.dark .thermo-amount { color: #eee; }
  .thermo-widget.dark .thermo-goal { color: #555; }
  .thermo-widget.dark .thermo-goal strong { color: #777; }
  .thermo-widget.dark .thermo-donors { color: #555; }
  .thermo-widget.dark .thermo-donors strong { color: #777; }

  /* -- Powered -- */
  .thermo-powered { font-size: 9px; color: #aaa; text-align: center; margin-top: 6px; }
  .thermo-widget.dark .thermo-powered { color: #444; }
  .thermo-powered strong { color: #888; font-weight: 700; font-size: 10px; }
  .thermo-widget.dark .thermo-powered strong { color: #555; }
  .thermo-powered a { color: inherit; text-decoration: none; }

  /* -- Small: horizontal pill -- */
  .thermo-small { flex-direction: row; gap: 12px; margin-left: 30px; align-items: center; background: #ffffff; border-radius: 99px; padding: 8px 16px 8px 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); max-width: 340px; width: 100%; }
  .thermo-small.dark { background: #222; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
  .thermo-small .thermo-body { flex-direction: row; }
  .thermo-small .thermo-tube { width: 6px; height: 30px; border-radius: 99px; }
  .thermo-small .thermo-fill { border-radius: 99px; }
  .thermo-small .thermo-bulb { display: none; }
  .thermo-small .thermo-pct { display: none; }
  .thermo-small .thermo-stats { display: flex; align-items: baseline; gap: 4px; text-align: left; flex: 1; }
  .thermo-small .thermo-amount { font-size: 14px; }
  .thermo-small .thermo-goal-inline { font-size: 11px; font-weight: 500; color: #888; white-space: nowrap; }
  .thermo-small .thermo-pct-badge { flex-shrink: 0; font-size: 11px; font-weight: 700; padding: 4px 11px; border-radius: 99px; background: var(--w-accent-light); color: var(--w-accent); }
  .thermo-small .thermo-powered { flex-shrink: 0; font-size: 8px; padding-right: 2px; margin-top: 0; }

  /* -- Medium: vertical card -- */
  .thermo-medium { background: #ffffff; border-radius: 10px; padding: 22px 24px 16px; max-width: 180px; width: 100%; gap: 14px; }
  .thermo-medium.dark { background: #222; }
  .thermo-medium .thermo-tube { width: 20px; height: 120px; }
  .thermo-medium .thermo-bulb { width: 44px; height: 44px; margin-top: -22px; }
  .thermo-medium .thermo-bulb-ring { width: 32px; height: 32px; }
  .thermo-medium .thermo-bulb-inner { width: 20px; height: 20px; }
  .thermo-medium .thermo-amount { font-size: 18px; }
  .thermo-medium .thermo-raised-label { font-size: 12px; }

  /* -- Large: taller vertical card -- */
  .thermo-large { background: #ffffff; border-radius: 12px; padding: 24px 28px 18px; max-width: 200px; width: 100%; gap: 16px; transition: box-shadow 0.2s, transform 0.2s; }
  .thermo-large:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.06); transform: translateY(-1px); }
  .thermo-large.dark { background: #222; }
  .thermo-large.dark:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.3); }
  .thermo-large .thermo-tube { width: 24px; height: 160px; }
  .thermo-large .thermo-fill { background: linear-gradient(0deg, var(--w-accent-dark), var(--w-accent)); }
  .thermo-large .thermo-bulb { width: 54px; height: 54px; margin-top: -27px; box-shadow: 0 3px 12px rgba(0,0,0,0.1); }
  .thermo-large .thermo-bulb-ring { width: 40px; height: 40px; }
  .thermo-large .thermo-bulb-inner { width: 26px; height: 26px; }
  .thermo-large .thermo-amount { font-size: 22px; }
  .thermo-large .thermo-raised-label { font-size: 12px; }

  /* -- Animations -- */
  @keyframes thermo-shimmer { 0% { background-position: 0 -200%; } 100% { background-position: 0 200%; } }
  .thermo-fill.animated { background: linear-gradient(0deg, var(--w-accent) 30%, var(--w-accent-light) 50%, var(--w-accent) 70%); background-size: 100% 200%; animation: thermo-shimmer 6s ease-in-out infinite; }
  .thermo-large .thermo-fill.animated { background: linear-gradient(0deg, var(--w-accent-dark) 30%, var(--w-accent-light) 50%, var(--w-accent) 70%); background-size: 100% 200%; animation: thermo-shimmer 6s ease-in-out infinite; }
`;

const ThermometerGoalWidget = ({
    campaign = 'Masjid Building Fund',
    raised = '$2,751',
    goal = '$5,000',
    pct = 55,
    donations = 142,
    size = 'medium',
    accentColor = '#0047AB',
    outline = 1,
    darkMode = false,
    showCampaign = false,
    showDonations = true,
}) => {
    const [fillHeight, setFillHeight] = useState(0);
    const [isAnimated, setIsAnimated] = useState(false);
    const [pctVisible, setPctVisible] = useState(false);

    useEffect(() => {
        let styleEl = document.getElementById('thermo-widget-styles');
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'thermo-widget-styles';
            styleEl.innerHTML = WIDGET_CSS;
            document.head.appendChild(styleEl);
        }
    }, []);

    useEffect(() => {
        setFillHeight(0);
        setIsAnimated(false);
        setPctVisible(false);
        const timer1 = setTimeout(() => setFillHeight(pct), 50);
        const timer2 = setTimeout(() => setPctVisible(true), 1000);
        const timer3 = setTimeout(() => setIsAnimated(true), 1400);
        return () => { clearTimeout(timer1); clearTimeout(timer2); clearTimeout(timer3); };
    }, [pct]);

    const accentDark = darken(accentColor, 0.2);
    const accentLight = lighten(accentColor, 0.9);

    const getOutlineStyle = () => {
        if (outline === 0) return { border: 'none' };
        if (outline === 1) return { border: `1px solid ${darkMode ? '#333' : '#e8eaed'}` };
        return { border: `${outline}px solid ${accentColor}` };
    };

    const widgetStyles = {
        ...getOutlineStyle(),
        '--w-accent': accentColor,
        '--w-accent-dark': accentDark,
        '--w-accent-light': accentLight,
    };

    const darkClass = darkMode ? ' dark' : '';

    const PoweredBy = () => (
            <a href="https://www.masjidal.com" className="thermo-powered" target="_blank" rel="noopener noreferrer">
                Powered by <strong>Masjidal</strong>
            </a>
    );

    const Bulb = () => (
        <div className="thermo-bulb">
            <div className="thermo-bulb-ring">
                <div className="thermo-bulb-inner" />
            </div>
        </div>
    );

    return (
        <div className="thermo-widget-container">
            {size === 'small' && (
                <div className={`thermo-widget thermo-small${darkClass}`} style={widgetStyles}>
                    <div className="thermo-body">
                        <div className="thermo-tube">
                            <div className={`thermo-fill ${isAnimated ? 'animated' : ''}`} style={{ height: `${fillHeight}%` }}></div>
                        </div>
                    </div>
                    <div className="thermo-stats">
                        <span className="thermo-amount">{raised}</span>
                        <span className="thermo-goal-inline">of {goal}</span>
                    </div>
                    <span className="thermo-pct-badge">{pct}%</span>
                    <PoweredBy />
                </div>
            )}

            {size === 'medium' && (
                <div className={`thermo-widget thermo-medium${darkClass}`} style={widgetStyles}>
                    <div className="thermo-body">
                        <div className="thermo-tube">
                            <div className={`thermo-fill ${isAnimated ? 'animated' : ''}`} style={{ height: `${fillHeight}%` }}>
                                <div className={`thermo-pct${pctVisible ? ' visible' : ''}`}>{pct}%</div>
                            </div>
                        </div>
                        <Bulb />
                    </div>
                    <div className="thermo-stats">
                        <div><span className="thermo-amount">{raised}</span> <span className="thermo-raised-label">raised</span></div>
                        <div className="thermo-goal">Goal: <strong>{goal}</strong></div>
                        {showDonations && <div className="thermo-donors"><strong>{donations}</strong> donations</div>}
                    </div>
                    <PoweredBy />
                </div>
            )}

            {size === 'large' && (
                <div className={`thermo-widget thermo-large${darkClass}`} style={widgetStyles}>
                    {showCampaign && <div className="thermo-campaign">{campaign}</div>}
                    <div className="thermo-body">
                        <div className="thermo-tube">
                            <div className={`thermo-fill ${isAnimated ? 'animated' : ''}`} style={{ height: `${fillHeight}%` }}>
                                <div className={`thermo-pct${pctVisible ? ' visible' : ''}`}>{pct}%</div>
                            </div>
                        </div>
                        <Bulb />
                    </div>
                    <div className="thermo-stats">
                        <div><span className="thermo-amount">{raised}</span> <span className="thermo-raised-label">raised</span></div>
                        <div className="thermo-goal">Goal: <strong>{goal}</strong></div>
                        {showDonations && <div className="thermo-donors"><strong>{donations}</strong> donations</div>}
                    </div>
                    <PoweredBy />
                </div>
            )}
        </div>
    );
};

export default ThermometerGoalWidget;